import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface DataTablePaginationProps<TData> {
  table: Table<TData>
}

export function DataTablePagination<TData>({
  table,
}: DataTablePaginationProps<TData>) {
  const { t } = useTranslation()
  const pageCount = table.getPageCount()
  const pageIndex = table.getState().pagination.pageIndex

  // Helper to generate page numbers to display
  const getPageButtons = () => {
    if (pageCount <= 5) {
      // Show all pages
      return Array.from({ length: pageCount }, (_, i) => i)
    }
    // More than 5 pages
    let start = Math.max(1, pageIndex - 1)
    let end = Math.min(pageCount - 2, pageIndex + 1)
    // Adjust if near start
    if (pageIndex <= 2) {
      start = 1
      end = 3
    }
    // Adjust if near end
    if (pageIndex >= pageCount - 3) {
      start = pageCount - 4
      end = pageCount - 2
    }
    return [start, end]
  }

  let pageButtons: (number | 'left-ellipsis' | 'right-ellipsis')[] = []
  if (pageCount <= 5) {
    pageButtons = Array.from({ length: pageCount }, (_, i) => i)
  } else {
    // Always show first page
    pageButtons.push(0)
    const [start, end] = getPageButtons()
    // Left ellipsis
    if (start > 1) pageButtons.push('left-ellipsis')
    // Middle page numbers
    for (let i = start; i <= end; i++) {
      pageButtons.push(i)
    }
    // Right ellipsis
    if (end < pageCount - 2) pageButtons.push('right-ellipsis')
    // Always show last page
    pageButtons.push(pageCount - 1)
  }

  const startRow =
    table.getState().pagination.pageIndex *
    table.getState().pagination.pageSize +
    1
  const endRow =
    table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
      table.getState().pagination.pageSize >
      table.getFilteredRowModel().rows.length
      ? table.getFilteredRowModel().rows.length
      : table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
      table.getState().pagination.pageSize
  const totalRows = table.getFilteredRowModel().rows.length

  return (
    <div className="flex items-center justify-between p-4">
      <div className="flex items-center space-x-2">
        <p className="text-sm text-gray-500">{t('common.pagination.show')}</p>
        <Select
          value={`${table.getState().pagination.pageSize}`}
          onValueChange={(value) => {
            table.setPageSize(Number(value))
          }}
        >
          <SelectTrigger className="h-8 w-[70px] bg-white">
            <SelectValue
              placeholder={`${table.getState().pagination.pageSize}`}
              className="text-gray-500"
            />
          </SelectTrigger>
          <SelectContent side="top">
            {[5, 10, 20, 30, 40, 50].map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`}>
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-sm text-gray-500">
          {t('common.pagination.showing')} {startRow}-{endRow}{' '}
          {t('common.pagination.from')} {totalRows}
        </p>
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          className="h-8 w-8 p-0"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          <span className="sr-only">{t('common.pagination.goToPreviousPage')}</span>
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>
        {/* Dynamic page buttons */}
        {pageButtons.map((btn, idx) => {
          if (btn === 'left-ellipsis') {
            return (
              <Button
                key={btn + idx}
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => table.setPageIndex(0)}
              >
                ...
              </Button>
            )
          }
          if (btn === 'right-ellipsis') {
            return (
              <Button
                key={btn + idx}
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => table.setPageIndex(pageCount - 1)}
              >
                ...
              </Button>
            )
          }
          return (
            <Button
              key={btn}
              variant={btn === pageIndex ? 'default' : 'outline'}
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => table.setPageIndex(Number(btn))}
              disabled={btn === pageIndex}
              style={{
                opacity: 1,
              }}
            >
              {Number(btn) + 1}
            </Button>
          )
        })}
        <Button
          variant="outline"
          className="h-8 w-8 p-0"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          <span className="sr-only">{t('common.pagination.goToNextPage')}</span>
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
